import React, { ReactNode, FC } from 'react';
import styled, { css, ThemeProvider, SimpleInterpolation } from 'styled-components';
import { AppcuesProvider } from '@blocs.appcues';
import { AuthenticatedRoute, AuthenticatedRouteProps } from '@blocs.auth';
import { Alert } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import Container from '@minecraft.container';
import GlobalStyles from '@minecraft.global-styles';
import { EnterpriseTheme } from '@minecraft.themes';
import Loader from '@minecraft.loader';
import ImpactedServiceAlert from '@blocs.impacted-service-alert';
import { FixedWidthPageStyles } from 'common/PageWrapper';
import Meta from './Meta';
import Header from './Header';
import AuthStore from './AuthStore';

/** Types */
type PageWrapperProps = {
  color: 'blue' | 'white';
  fixedWidth: boolean;
  updater: Record<string, any>;
  shouldRenderPage: boolean;
  children: ReactNode;
  pageTitle?: string;
  shouldShowLoader?: boolean;
  // If true, hideHeader hides the header at the top of the page. Default value is false.
  hideHeader?: boolean;
};

export interface PrivatePageProps extends AuthenticatedRouteProps {
  color?: 'blue' | 'white';
  fixedWidth?: boolean;
  pageTitle?: string;
  // If true, hideHeader hides the header at the top of the page. Default value is false.
  hideHeader?: boolean;
}

/** Styled Components */
const StyledContainer = styled(Container)<{ $fixedWidth: boolean }>`
  padding: 0;
  margin: 0;
  ${(props): SimpleInterpolation =>
    props.$fixedWidth &&
    css`
      ${FixedWidthPageStyles}
    `};
`;

const CenterText = styled.div`
  height: 80vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackgroundContainer = styled.div<{ $color: 'blue' | 'white' }>`
  min-height: 100vh;
  background: ${(props): string => {
    if (props.$color === 'white') return undefined;

    return props.theme.colors.accent.ltBlue;
  }};
`;

/** Help Components */
const PageWrapper: FC<PageWrapperProps> = ({
  pageTitle,
  color,
  fixedWidth,
  updater,
  shouldRenderPage,
  children,
  shouldShowLoader,
  hideHeader = false,
}) => {
  return (
    <>
      <GlobalStyles />
      <Meta pageTitle={pageTitle} />
      <Alert.GlobalAlertComponent />
      <BackgroundContainer data-testid="reps-bg-container" $color={color}>
        {!hideHeader && <Header updater={updater} />}
        <StyledContainer fluid $fixedWidth={fixedWidth}>
          {(shouldShowLoader || !shouldRenderPage) && <Loader />}
          {shouldRenderPage && !shouldShowLoader && <AppcuesProvider>{children}</AppcuesProvider>}
        </StyledContainer>
      </BackgroundContainer>
    </>
  );
};

/** Main Component */
const PrivatePage: FC<PrivatePageProps> = ({
  pageTitle,
  color = 'blue',
  fixedWidth = true,
  hideHeader = false,
  children,
  ...routeProps
}) => {
  const { t } = useTranslation();

  return (
    <AuthenticatedRoute {...routeProps}>
      <ThemeProvider theme={EnterpriseTheme}>
        <AuthStore>
          {({
            handleDivisionChange,
            handleLogoutClick,
            selectedDivisionId,
            shouldRenderPage,
            shouldShowLoader,
          }): JSX.Element => {
            return selectedDivisionId || shouldShowLoader ? (
              <>
                <ImpactedServiceAlert />
                <PageWrapper
                  pageTitle={pageTitle}
                  color={color}
                  fixedWidth={fixedWidth}
                  shouldRenderPage={shouldRenderPage}
                  shouldShowLoader={shouldShowLoader}
                  updater={{
                    handleDivisionChange,
                    handleLogoutClick,
                  }}
                  hideHeader={hideHeader}
                >
                  {children}
                </PageWrapper>
              </>
            ) : (
              <CenterText>
                {t('reps:privatePage.message')}
                <a href="mailto:support@castingnetworks.com">{t('reps:privatePage.link')}</a>.
              </CenterText>
            );
          }}
        </AuthStore>
      </ThemeProvider>
    </AuthenticatedRoute>
  );
};

export default PrivatePage;
